
import { createContext, useContext, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { Collection, CollectionsContextType } from "./types";
import { ensureValidCollectionName, getCollectionDisplayName as getDisplayName } from "./utils";
import { useCollectionsQuery } from "./hooks/useCollectionsQuery";
import { useCollectionOperations } from "./hooks/useCollectionOperations";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Create context
const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

// Provider component
export const CollectionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Use the query hook
  const { 
    collections: rawCollections, 
    isLoading, 
    refreshCollections, 
    getCollectionById, 
    updateCollectionPosition 
  } = useCollectionsQuery(user?.id);

  // Memoize collections sorting to avoid unnecessary re-sorting
  const collections = useMemo(() => {
    console.log("CollectionsContext: Sorting collections by position");
    // Create a new array to avoid mutations and ensure consistent sorting
    return [...rawCollections].sort((a, b) => a.position - b.position);
  }, [rawCollections]);
  
  // Optimize periodic refresh - reduce from 30s to 5 minutes and only when page is visible
  useEffect(() => {
    // Initial refresh
    refreshCollections();
    
    // Set up less frequent interval for periodic refresh
    const intervalId = setInterval(() => {
      // Only refresh if page is visible
      if (!document.hidden) {
        console.log("CollectionsContext: Periodic refresh of collections");
        refreshCollections();
      }
    }, 300000); // Every 5 minutes instead of 30 seconds
    
    return () => clearInterval(intervalId);
  }, [refreshCollections]);

  // Use the operations hook
  const { 
    operation, 
    startEditCollection, 
    startDeleteCollection, 
    closeOperation 
  } = useCollectionOperations(getCollectionById);

  // Collection display name helper - memoized
  const getCollectionDisplayName = useCallback((collection: Partial<Collection>): string => {
    return getDisplayName(collection, getCollectionById, collections);
  }, [getCollectionById, collections]);

  // Handle collection updated - optimized
  const handleCollectionUpdated = useCallback(() => {
    console.log("Collection updated, refreshing data");
    refreshCollections();
    closeOperation();
  }, [refreshCollections, closeOperation]);

  // Handle collection deleted - optimized
  const handleCollectionDeleted = useCallback(() => {
    console.log("Collection deleted, refreshing data");
    refreshCollections();
    closeOperation();
  }, [refreshCollections, closeOperation]);

  // Toggle collection visibility
  const toggleCollectionVisibility = useCallback(async (collectionId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const collection = getCollectionById(collectionId);
      if (!collection) {
        console.error("Collection not found:", collectionId);
        return false;
      }
      
      const newHiddenState = !collection.hidden;
      
      const { error } = await supabase
        .from('collections')
        .update({ hidden: newHiddenState })
        .eq('id', collectionId)
        .eq('user_id', user.id);
        
      if (error) {
        console.error("Error toggling collection visibility:", error);
        toast.error("Failed to update collection visibility");
        return false;
      }
      
      toast.success(`Collection ${newHiddenState ? 'hidden' : 'visible'}`);
      refreshCollections();
      return true;
    } catch (e) {
      console.error("Error in toggleCollectionVisibility:", e);
      toast.error("An unexpected error occurred");
      return false;
    }
  }, [user, getCollectionById, refreshCollections]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    collections, // This is now always sorted by position
    isLoading,
    refreshCollections,
    operation,
    startEditCollection,
    startDeleteCollection,
    closeOperation,
    handleCollectionUpdated,
    handleCollectionDeleted,
    getCollectionDisplayName,
    getCollectionById,
    updateCollectionPosition,
    toggleCollectionVisibility
  }), [
    collections,
    isLoading,
    refreshCollections,
    operation,
    startEditCollection,
    startDeleteCollection,
    closeOperation,
    handleCollectionUpdated,
    handleCollectionDeleted,
    getCollectionDisplayName,
    getCollectionById,
    updateCollectionPosition,
    toggleCollectionVisibility
  ]);

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
};

// Hook to use context
export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (context === undefined) {
    throw new Error("useCollections must be used within a CollectionsProvider");
  }
  return context;
};
